import * as React from "react";
import { useCallback } from "react";
import { ProcedureSelect } from "../../../organisms/smart-apply/procedure-select";
import {
  ContentsHeader,
  DateRangeInput,
  KebabMenu,
  MenuItemProps,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { Sort } from "@graffer-inc/smart-apply";
import { ApplicationsContextProps } from "../../../../application/service/smart-apply/applications/applications-service";
import { useModal } from "../../../../utils/custom-hooks";
import { ITextInputElement } from "@graffer-inc/form-schema";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { SmartApplyR } from "../route";
import { useAuthContextV2 } from "../../../../auth-state";
import { formatDate } from "../../../../utils/date-utils";

export const ApplicationsHeader: React.FC<
  Pick<
    ApplicationsContextProps,
    | "selectedProcedure"
    | "setSelectedProcedureId"
    | "setDateRange"
    | "sort"
    | "setSort"
    | "searchApplications"
  > & {
    downloadCsvModal: ReturnType<typeof useModal>;
    tenantProcessResultUpdateModal: ReturnType<typeof useModal>;
    inputElements?: ITextInputElement[];
    procedureId?: string;
    selectedIds: string[];
    dateFrom?: Date;
    dateTo?: Date;
  }
> = ({
  selectedProcedure,
  setSelectedProcedureId,
  dateFrom,
  dateTo,
  setDateRange,
  sort,
  setSort,
  searchApplications,
  downloadCsvModal,
  tenantProcessResultUpdateModal,
  inputElements,
  procedureId,
  selectedIds,
}) => {
  const history = useHistory();
  const authState = useAuthContextV2();

  const kebabItems = [];
  if (
    authState.auth.checkGroupPermissionWithoutTarget([
      "applications.removal_tasks_read",
    ])
  ) {
    kebabItems.push({
      text: "申請削除タスク一覧を見る",
      anchor: ({ children }) => (
        <button
          onClick={() => {
            history.push(SmartApplyR.ApplicationRemovalTaskList.link({}));
          }}
        >
          {children}
        </button>
      ),
    } as MenuItemProps);
  }

  const setFilterValue = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      setDateRange({
        from: startDate ?? undefined,
        to: endDate ?? undefined,
      });
    },
    [setDateRange],
  );

  return (
    <ContentsHeader
      title={"申請一覧"}
      border={false}
      caption={[
        <div
          key={"applicationsProcedureSelect"}
          className="applications-procedure-select"
          data-testid="procedure-name"
          data-for-rpa-id={"select-procedure"}
          style={{
            width: 320,
          }}
        >
          <ProcedureSelect
            key={`procedureSelect-${selectedProcedure?.id}`}
            selected={selectedProcedure}
            onSelect={setSelectedProcedureId}
          />
        </div>,
        <DateRangeInput
          key={`applicationsDateRangeInput-${dateFrom && formatDate(dateFrom)}-${
            dateTo && formatDate(dateTo)
          }`}
          isSmall
          startDate={dateFrom}
          endDate={dateTo}
          setValue={setFilterValue}
        />,
        !procedureId ? (
          <div className={classNames("select", "applications-sort-select")}>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as Sort);
              }}
            >
              <option value={"desc" as Sort}>新しい順</option>
              <option value={"asc" as Sort}>古い順</option>
            </select>
          </div>
        ) : undefined,
        <SecondaryButton
          key={"applicationsSearchApplicationButton"}
          text={"更新"}
          onClick={searchApplications}
        />,
      ]}
      buttons={[
        [
          inputElements ? (
            <SecondaryButton
              text="一括入力"
              icon="LoginIcon"
              onClick={tenantProcessResultUpdateModal.open}
            />
          ) : undefined,
          procedureId ? (
            <SecondaryButton
              data-for-rpa-id={"csv-output"}
              icon={"DownloadIcon"}
              text="CSV一括出力"
              onClick={downloadCsvModal.open}
              disabled={selectedIds.length > 0}
            />
          ) : undefined,
          kebabItems.length !== 0 ? (
            <KebabMenu
              key={"applicationKebabMenu"}
              items={[
                {
                  title: "申請削除",
                  menuItems: kebabItems,
                },
              ]}
            />
          ) : undefined,
        ],
      ]}
      captionDirection="row"
    />
  );
};
